import { useCallback, useEffect, useState } from 'react'
import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { Layout } from 'antd'
import './ChatLayout.scss'

const { Content, Sider } = Layout

type ChatLayoutProps = {
    header?: ReactNode
    content: ReactNode
    sider?: ReactNode
    siderWidth?: number
    className?: string
    headerClassName?: string
    contentClassName?: string
    siderClassName?: string
    contentWrapped?: boolean
    resizableSider?: boolean
    minSiderWidth?: number
    maxSiderWidth?: number
}

function joinClassNames(...classNames: Array<string | undefined>) {
    return classNames.filter(Boolean).join(' ')
}

export default function ChatLayout({
    header,
    content,
    sider,
    siderWidth = 400,
    className,
    headerClassName,
    contentClassName,
    siderClassName,
    contentWrapped = true,
    resizableSider = false,
    minSiderWidth = 320,
    maxSiderWidth = 760,
}: ChatLayoutProps) {
    const [currentSiderWidth, setCurrentSiderWidth] = useState(siderWidth)
    useEffect(() => {
        setCurrentSiderWidth(siderWidth)
    }, [siderWidth])
    const startResize = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
        event.preventDefault()
        const startX = event.clientX
        const startWidth = currentSiderWidth
        const resize = (moveEvent: MouseEvent) => {
            // The right sider grows while its left border is dragged left.
            const nextWidth = startWidth + startX - moveEvent.clientX
            setCurrentSiderWidth(Math.min(maxSiderWidth, Math.max(minSiderWidth, nextWidth)))
        }
        const stopResize = () => {
            document.removeEventListener('mousemove', resize)
            document.removeEventListener('mouseup', stopResize)
        }
        document.addEventListener('mousemove', resize)
        document.addEventListener('mouseup', stopResize)
    }, [currentSiderWidth, maxSiderWidth, minSiderWidth])

    return (
        <div className={joinClassNames('chat-layout', className)}>
            <Layout className="chat-layout__container">
                <Content className='chat-layout__content'>
                    {header != null ? (
                        <div className={joinClassNames('page-header flex flex-between', headerClassName)}>
                            {header}
                        </div>
                    ) : null}
                    {contentWrapped ? (
                        <div className={joinClassNames('page-content', contentClassName)}>
                            {content}
                        </div>
                    ) : (
                        content
                    )}
                </Content>
                {sider != null ? (
                    <>
                        {resizableSider ? (
                            <div
                                className="chat-layout__sider-resize-handle"
                                onMouseDown={startResize}
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="调整问答面板宽度"
                            >
                                <span aria-hidden="true">⋮</span>
                            </div>
                        ) : null}
                        <Sider
                            width={resizableSider ? currentSiderWidth : siderWidth}
                            theme="light"
                            className={joinClassNames('chat-layout__sider', siderClassName)}
                        >
                            {sider}
                        </Sider>
                    </>
                ) : null}
            </Layout>
        </div>
    )
}
